import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined'

import { DetailButton } from '../../components/common/detail'
import DocumentModal from '../../components/containers/DocumentModal'
import '../../components/common/detail/detail.css'

type DocLike = {
  originalName?: string
  path?: string
  mimeType?: string
} | null

type Props = {
  hasSupportId: boolean
  document: DocLike
  loading: boolean
  missingOrBroken: boolean
  canManage: boolean
  saving?: boolean
  onSaveFile: (file: File) => Promise<void>
  /** Optional copy overrides (maturity proof, etc.) */
  attachedHint?: string
  emptyTitle?: string
  emptyHint?: string
  emptyReadonly?: string
  accept?: string
}

const SupportDocumentPanel: React.FC<Props> = ({
  hasSupportId,
  document,
  loading,
  missingOrBroken,
  canManage,
  saving = false,
  onSaveFile,
  attachedHint,
  emptyTitle,
  emptyHint,
  emptyReadonly,
  accept = 'application/pdf,.pdf,.doc,.docx',
}) => {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [pendingName, setPendingName] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [localError, setLocalError] = useState('')

  const showFile = hasSupportId && !missingOrBroken && !!document?.originalName

  const pickFile = () => inputRef.current?.click()

  const acceptFile = async (file: File | undefined | null) => {
    if (!file) return
    const okPdf = !file.type || file.type === 'application/pdf'
    const okDoc =
      file.type === 'application/msword' ||
      file.type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      /\.(pdf|doc|docx)$/i.test(file.name)
    if (!okPdf && !okDoc) {
      setLocalError(
        t('documentSelector.pdfOnly', {
          defaultValue: 'Only PDF or Word files are allowed.',
        })
      )
      return
    }
    setLocalError('')
    setPendingName(file.name)
    try {
      await onSaveFile(file)
    } finally {
      setPendingName(null)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    void acceptFile(e.target.files?.[0])
  }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    void acceptFile(e.dataTransfer.files?.[0])
  }

  return (
    <div className="support-panel">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        hidden
        onChange={onInputChange}
      />

      {loading && !showFile ? (
        <div className="detail-loading">{t('document.loading')}</div>
      ) : showFile ? (
        <div className="support-file-card">
          <div className="support-file-main">
            <span className="support-file-ico" aria-hidden>
              <InsertDriveFileOutlinedIcon sx={{ fontSize: 22 }} />
            </span>
            <div className="support-file-meta">
              <p className="support-file-name">{document?.originalName}</p>
              <p className="support-file-hint">
                {attachedHint ||
                  t('taskDetailsPage.support.attached', {
                    defaultValue: 'Attached support document (PDF)',
                  })}
              </p>
            </div>
          </div>
          <div className="support-file-actions">
            <DetailButton
              variant="ghost"
              size="sm"
              onClick={() => setModalOpen(true)}
            >
              <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
              {t('buttons.view', { defaultValue: 'View' })}
            </DetailButton>
            {canManage ? (
              <DetailButton size="sm" onClick={pickFile} disabled={saving}>
                <SwapHorizOutlinedIcon sx={{ fontSize: 16 }} />
                {saving
                  ? t('saving', { defaultValue: 'Saving…' })
                  : t('buttons.replace', { defaultValue: 'Replace' })}
              </DetailButton>
            ) : null}
          </div>
          <DocumentModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            document={document}
          />
        </div>
      ) : (
        <div
          className={`support-dropzone${dragOver ? ' is-drag' : ''}`}
          onDragOver={(e) => {
            e.preventDefault()
            setDragOver(true)
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={canManage ? onDrop : undefined}
        >
          <span className="support-drop-ico" aria-hidden>
            <CloudUploadOutlinedIcon sx={{ fontSize: 32 }} />
          </span>
          <p className="support-drop-title">
            {missingOrBroken && hasSupportId
              ? t('taskDetailsPage.support.missingTitle', {
                  defaultValue: 'Support file not found',
                })
              : emptyTitle ||
                t('taskDetailsPage.noSupportDocument', {
                  defaultValue: 'No support document',
                })}
          </p>
          <p className="support-drop-msg">
            {canManage
              ? emptyHint ||
                t('taskDetailsPage.support.uploadHint', {
                  defaultValue:
                    'Upload a PDF to attach proof or supporting material to this task.',
                })
              : emptyReadonly ||
                t('taskDetailsPage.support.noneReadonly', {
                  defaultValue: 'No support document is attached to this task.',
                })}
          </p>
          {canManage ? (
            <DetailButton onClick={pickFile} disabled={saving}>
              <CloudUploadOutlinedIcon sx={{ fontSize: 16 }} />
              {saving || pendingName
                ? t('saving', { defaultValue: 'Saving…' })
                : t('buttons.uploadFile', { defaultValue: 'Upload PDF' })}
            </DetailButton>
          ) : null}
          {localError ? (
            <p className="support-drop-error">{localError}</p>
          ) : null}
        </div>
      )}
    </div>
  )
}

export default SupportDocumentPanel
